import { http } from "../utils/http";
import { API_ENDPOINTS } from "../config";
import moment from "moment";
import { getAppointmentsFilter, getSlotFilter } from "../Shared/Utils";
import { store } from "../State/store/configureStore";
import { closeProgress, showProgress } from "../State/actions/uploadProgress";
import I18nProvider from "./I18nProvider";
import Messages from "../Shared/Messages";
import { isFacilityAdmin } from "../security/roles";

class AppointmentsService {
  static getAppointmentStatusMap() {
    const intl = I18nProvider.getIntlProvider();
    return {
      PROPOSED: intl.formatMessage(Messages.proposed),
      SCHEDULED: intl.formatMessage(Messages.scheduled),
      BOOKED: intl.formatMessage(Messages.booked),
      FULFILLED: intl.formatMessage(Messages.fulfilled),
      NOSHOW: intl.formatMessage(Messages.no_show),
      REBOOKED: intl.formatMessage(Messages.rebooked),
    };
  }

  static getSlotStatusMap() {
    const intl = I18nProvider.getIntlProvider();
    return {
      FREE: intl.formatMessage(Messages.fa_slot_status_free),
      BUSY_UNAVAILABLE: intl.formatMessage(Messages.fa_slot_status_busyua),
      BUSY_TENTATIVE: intl.formatMessage(Messages.fa_slot_status_busytt),
      ENTERED_IN_ERROR: intl.formatMessage(Messages.fa_slot_status_error),
      BUSY: intl.formatMessage(Messages.fa_slot_status_busy),
      BUSY_RESERVED: intl.formatMessage(Messages.fa_slot_status_busy_reserved),
    };
  }

  static getAppointmentStatus() {
    const intl = I18nProvider.getIntlProvider();
    return [
      {
        value: "PROPOSED",
        label: intl.formatMessage(Messages.proposed),
      },
      {
        value: "SCHEDULED",
        label: intl.formatMessage(Messages.scheduled),
      },
      {
        value: "BOOKED",
        label: intl.formatMessage(Messages.booked),
      },
      {
        value: "FULFILLED",
        label: intl.formatMessage(Messages.fulfilled),
      },
      {
        value: "NOSHOW",
        label: intl.formatMessage(Messages.no_show),
      },
      {
        value: "REBOOKED",
        label: intl.formatMessage(Messages.rebooked),
      },
    ];
  }

  static getSlotStatus() {
    const intl = I18nProvider.getIntlProvider();
    return [
      {
        label: intl.formatMessage(Messages.fa_slot_status_free),
        value: "FREE",
      },
      {
        label: intl.formatMessage(Messages.fa_slot_status_busyua),
        value: "BUSY_UNAVAILABLE",
      },
      {
        label: intl.formatMessage(Messages.fa_slot_status_busytt),
        value: "BUSY_TENTATIVE",
      },
      {
        label: intl.formatMessage(Messages.fa_slot_status_error),
        value: "ENTERED_IN_ERROR",
      },
    ];
  }

  static formatDateToDDMMYYYY(date) {
    if (!date) return null;

    if (typeof date === "string" && /^\d{8}$/.test(date)) {
      return date;
    }

    return moment(date).format("DDMMYYYY");
  }

  static getAllSchedules() {
    return http
      .request(
        API_ENDPOINTS.appointmentSchedule + "?page=0&size=1000&allStatus=True"
      )
      .then((data) => {
        if (data && data._embedded) {
          return data._embedded.appointmentSchedules || [];
        }
        return [];
      });
  }

  static createSchedule(data) {
    return http.request(API_ENDPOINTS.appointmentSchedule, "post", data);
  }

  static getASchedule(scheduleId, showAllSlots) {
    let api = API_ENDPOINTS.appointmentSchedule + "/" + scheduleId;
    if (showAllSlots) {
      api = api + "?showAllSlots=true";
    }

    return http.request(api);
  }

  static editSchedule(scheduleId, scheduleData) {
    return http.request(
      API_ENDPOINTS.appointmentSchedule + "/" + scheduleId,
      "patch",
      scheduleData
    );
  }

  static deleteSchedule(scheduleId) {
    return http.request(
      API_ENDPOINTS.appointmentSchedule + "/" + scheduleId,
      "delete"
    );
  }

  static async createAppointment(appointmentData) {
    const response = await http.request(
      API_ENDPOINTS.appointments,
      "post",
      appointmentData
    );
    if (!response) {
      return;
    }
    if (Array.isArray(response)) {
      response.forEach((appt) => {
        appt.start = new Date(appt.start);
        appt.end = new Date(appt.end);
      });
    } else {
      if (response.start) {
        response.start = new Date(response.start);
        response.end = new Date(response.end);
      }
    }
    return response;
  }

  static createApppointmentSeriesPreview(patientId, appointmentData) {
    let api = API_ENDPOINTS.appointmentSeriesPreview(patientId);
    if (appointmentData.ignoreFirstAppt !== null) {
      api += `?ignoreFirstAppt=${appointmentData.ignoreFirstAppt}`;
    }
    return http.request(api, "post", appointmentData);
  }

  static rescheduleAppointmentSeriesPreview(patientId, appointmentData) {
    let api = API_ENDPOINTS.appointmentSeries(patientId) + `/reschedule`;
    return http.request(api, "post", appointmentData);
  }

  static createAppointmentSeries(patientId, appointmentData) {
    let submitData = {
      appointments: [...appointmentData],
    };
    return http.request(
      API_ENDPOINTS.appointmentSeries(patientId),
      "post",
      submitData
    );
  }
  static async getCalendarAppointments(
    filter = {},
    scheduleId,
    patientId,
    allStatus,
    start,
    end,
    seriesId
  ) {
    // Check user role - FACILITY_ADMIN_ROLE should use slots API instead
    const state = store.getState();
    const userRole = state.user?.role;

    if (isFacilityAdmin(userRole)) {
      console.log(
        "[API] 🔒 FACILITY_ADMIN detected - using slots API instead of appointments calendar"
      );

      // Default date-range to current month if both start and end are undefined
      if (start === undefined && end === undefined) {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        // Format as DDMMYYYY - extracted as utility function
        const formatDate = (date) => {
          const day = date.getDate().toString().padStart(2, "0");
          const month = (date.getMonth() + 1).toString().padStart(2, "0");
          const year = date.getFullYear().toString();
          return `${day}${month}${year}`;
        };

        start = formatDate(startOfMonth);
        end = formatDate(endOfMonth);
      }

      // Use slots API for facility admins
      try {
        return await this.getSlots(
          filter,
          scheduleId,
          start,
          end,
          false,
          allStatus
        );
      } catch (slotsError) {
        console.error(
          "[API] ❌ Slots API failed for FACILITY_ADMIN:",
          slotsError
        );
        throw slotsError;
      }
    }

    // For non-facility admin users, try the lightweight calendar API first
    try {
      //console.log("[API] Attempting lightweight calendar API...");

      //default date-range to current month if both start and end are undefined
      if (start === undefined && end === undefined) {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        // Format as DDMMYYYY - extracted as utility function
        const formatDate = (date) => {
          const day = date.getDate().toString().padStart(2, "0");
          const month = (date.getMonth() + 1).toString().padStart(2, "0");
          const year = date.getFullYear().toString();
          return `${day}${month}${year}`;
        };

        start = formatDate(startOfMonth);
        end = formatDate(endOfMonth);
      }

      // Build the lightweight API URL
      const calendarApi = `${
        API_ENDPOINTS.appointmentsCalendar
      }?start=${start}&end=${end}&size=1000${getAppointmentsFilter(
        filter,
        scheduleId,
        patientId,
        allStatus,
        null, // Don't pass start again
        null, // Don't pass end again
        seriesId
      )}`;

      //console.log("[API] Lightweight calendar API call:", {
      //  api: calendarApi,
      //  start,
      //  end,
      //  filter,
      //});

      const data = await http.request(calendarApi);
      const appointments = data?._embedded?.appointments ?? [];

      //console.log(
      //  "[API] ✅ Lightweight calendar API success:",
      //  appointments.length,
      //  "appointments"
      //);

      // Transform data to match existing structure
      return appointments.map((appointment) => ({
        ...appointment,
        start: new Date(appointment.start),
        end: new Date(appointment.end),
      }));
    } catch (lightweightError) {
      console.warn(
        "[API] ⚠️ Lightweight calendar API failed, falling back to existing API:",
        {
          error: lightweightError.message,
          status: lightweightError.status || "unknown",
          filter,
          start,
          end,
        }
      );

      // Fallback to existing API with try-catch for better error handling
      try {
        console.log("[API] 🔄 Attempting fallback to getAllAppointments...");
        const fallbackResult = await this.getAllAppointments(
          filter,
          scheduleId,
          patientId,
          allStatus,
          start,
          end,
          seriesId
        );
        console.log(
          "[API] ✅ Fallback API success:",
          fallbackResult.length,
          "appointments"
        );
        return fallbackResult;
      } catch (fallbackError) {
        console.error(
          "[API] ❌ Both lightweight and fallback APIs failed:",
          fallbackError
        );
        throw fallbackError; // Re-throw the fallback error
      }
    }
  }

  static async getAllAppointments(
    filter = {},
    scheduleId,
    patientId,
    allStatus,
    start,
    end,
    seriesId
  ) {
    //default date-range to current month if both start and end are undefined
    if (start === undefined && end === undefined) {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      // Format as DDMMYYYY - extracted as utility function
      const formatDate = (date) => {
        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const year = date.getFullYear().toString();
        return `${day}${month}${year}`;
      };

      start = formatDate(startOfMonth);
      end = formatDate(endOfMonth);
    }

    const api = `${API_ENDPOINTS.appointments}?size=1000${getAppointmentsFilter(
      filter,
      scheduleId,
      patientId,
      allStatus,
      start,
      end,
      seriesId
    )}`;

    console.log("[API Debug] getAllAppointments called:", {
      api,
      start,
      end,
      filter,
    });

    try {
      const data = await http.request(api);
      const appointments = data?._embedded?.appointments ?? [];

      // Use map instead of forEach for functional approach (if you want immutability)
      // Or keep forEach if you prefer mutating the original objects
      return appointments.map((appointment) => ({
        ...appointment,
        start: new Date(appointment.start),
        end: new Date(appointment.end),
      }));

      // Alternative: keep the original forEach approach if mutation is preferred
      // appointments.forEach((appointment) => {
      //   appointment.start = new Date(appointment.start);
      //   appointment.end = new Date(appointment.end);
      // });
      // return appointments;
    } catch (error) {
      console.error("Error fetching appointments:", error);
      throw error; // Re-throw or handle as needed
    }
  }

  static getAppointment(appointmentId) {
    return http.request(API_ENDPOINTS.appointments + "/" + appointmentId);
  }

  static getAllTemplates(type) {
    let api = API_ENDPOINTS.appointmentTemplates + "?page=0&size=1000";
    if (type) {
      if (type === "group") {
        api += "&group=true";
      } else {
        api += "&group=false";
      }
    }

    return http.request(api).then((data) => {
      if (data && data._embedded) {
        return data._embedded.appointmentTemplates || [];
      }
      return [];
    });
  }

  static createTemplate(data) {
    return http.request(API_ENDPOINTS.appointmentTemplates, "post", data);
  }

  // MYP-9072: with patientId, the backend fills attributes mapped to a patient attribute
  static getATemplate(templateId, patientId) {
    let api = API_ENDPOINTS.appointmentTemplates + "/" + templateId;
    if (patientId) {
      api += "?patientId=" + encodeURIComponent(patientId);
    }
    return http.request(api);
  }

  static editTemplate(templateId, templateData) {
    return http.request(
      API_ENDPOINTS.appointmentTemplates + "/" + templateId,
      "patch",
      templateData
    );
  }

  static deleteTemplate(templateId) {
    return http.request(
      API_ENDPOINTS.appointmentTemplates + "/" + templateId,
      "delete"
    );
  }

  static editAppointment(appointmentId, data, rescheduleSeries) {
    let api = API_ENDPOINTS.appointments + "/" + appointmentId;
    let hasParams = false;

    if (data.editReason) {
      api += `?editReason=${encodeURIComponent(data.editReason)}`;
      hasParams = true;
    }
    if (rescheduleSeries) {
      api += `${hasParams ? "&" : "?"}rescheduleSeries=true`;
    }
    return http.request(api, "patch", data);
  }

  static cancelAppointment(appointmentId, screeningResult, seriesId, reason) {
    let api = API_ENDPOINTS.appointments + "/" + appointmentId;
    let hasParams = false;

    if (screeningResult) {
      api += `?screeningResult=${screeningResult}`;
      hasParams = true;
    }
    if (seriesId) {
      api += `${hasParams ? "&" : "?"}seriesId=${seriesId}`;
      hasParams = true;
    }
    if (reason) {
      api += `${hasParams ? "&" : "?"}cancelReason=${encodeURIComponent(
        reason
      )}`;
      hasParams = true;
    }
    return http.request(api, "delete");
  }

  static createAppointmentFromTemplate(data) {
    return http.request(API_ENDPOINTS.appointmentsFromTemplate, "post", data);
  }

  static getTodaysAppointmentsForStaff(staffId) {
    return http
      .request(
        API_ENDPOINTS.appointments +
          `?staffId=${staffId}&page=0&size=1000&todaysAppointmentsOnly=true`
      )
      .then((data) => {
        let appointments = [];
        if (data && data._embedded) {
          appointments = data._embedded.appointments || [];
        }

        appointments = appointments.filter((appointment) =>
          moment().isBefore(moment(appointment.end))
        );
        return appointments.sort((a, b) => a.start - b.start);
      });
  }

  static getPublishedSchedules() {
    return http
      .request(API_ENDPOINTS.publishedSchedules + `?page=0&size=100`)
      .then((data) => {
        let schedules = [];
        if (data && data._embedded) {
          schedules = data._embedded.appointmentPublishedSchedules || [];
        }
        return schedules;
      });
  }

  static shareScheduleLink = (patientId, link) => {
    return http.request(API_ENDPOINTS.sendMessageToPatients(), "post", {
      patientIds: [patientId],
      campaignMode: "SELECTED_PATIENTS",
      smsMessage: "Book your appointment here " + link,
      emailSubject: "Book your appointment here",
      emailMessage:
        '<div>Hello _PatientName, you can <a href="' +
        link +
        '" style="color: rgb(0, 139, 128);">book your appointment</a>. </div><div style="margin-top: 20px;">From </div><div>_FacilityName</div>',
    });
  };

  static loadTemplateParametersForComments() {
    return http
      .request(API_ENDPOINTS.loadTemplateParameters("ALL"))
      .then((data) => {
        return data && data._embedded ? data._embedded.valueParameters : [];
      });
  }

  static getSlots(filter = {}, scheduleId, start, end, countOnly, allStatus) {
    const intl = I18nProvider.getIntlProvider();
    let api = API_ENDPOINTS.slots + "?";
    api += getSlotFilter(filter, scheduleId, start, end, countOnly, allStatus);

    console.log("[API] 🎯 getSlots called with:", {
      filter,
      scheduleId,
      start,
      end,
      countOnly,
      allStatus,
    });
    console.log("[API] 🌐 Slots API URL:", api);

    return http.request(api).then((slots = []) => {
      if (countOnly) {
        slots.forEach((slot) => {
          slot.allDay = true;
          slot.start = new Date(slot.statDate);
          slot.end = new Date(slot.statDate);
          slot.title =
            slot.count === 1
              ? intl.formatMessage(Messages.one_slot)
              : intl.formatMessage(Messages.count_slots, { count: slot.count });
        });
      } else {
        slots.forEach((slot) => {
          slot.start = new Date(slot.start);
          slot.end = new Date(slot.end);
        });
      }
      return slots;
    });
  }

  static createSlots(scheduleId, slotsData, options = {}) {
    let url = API_ENDPOINTS.scheduleSlots(scheduleId);
    if (options.allowDuplicateSlotCreation) {
      url += "?allowDuplicateSlotCreation=true";
    }
    return http.request(url, "post", slotsData);
  }

  static sendStartNotification(appointmentId, data) {
    return http.request(
      API_ENDPOINTS.appointmentStartNotification(appointmentId),
      "post",
      data
    );
  }

  static editSlot(slotId, slotData) {
    return http.request(API_ENDPOINTS.slots + "/" + slotId, "patch", slotData);
  }

  static deleteSlots(data) {
    let api = API_ENDPOINTS.slots;
    if (data.slotId) {
      api = api + "?slotId=" + data.slotId;
    } else {
      api =
        api +
        "?scheduleId=" +
        data.scheduleId +
        "&start=" +
        moment(new Date(data.start)).format("DDMMYYYY") +
        "&end=" +
        moment(new Date(data.end)).format("DDMMYYYY");
      if (data.participant) {
        if (data.participant.staffId) {
          api = api + "&staffId=" + data.participant.staffId;
        }
        if (data.participant.masterDataValue) {
          api =
            api + "&masterDataValue=" + data.participant.masterDataValue.code;
        }
      }
    }

    return http.request(api, "delete");
  }

  static fetchAppointmentAnonymous(appointmentId, pId, uId) {
    return http.request(
      API_ENDPOINTS.anonAppointment(appointmentId) + `?pid=${pId}&uid=${uId}`
    );
  }

  static cancelAppointmentAnonymous(appointmentId, pId, uId, reason) {
    let api =
      API_ENDPOINTS.anonAppointment(appointmentId) + `?pid=${pId}&uid=${uId}`;
    if (reason) {
      api += `&cancelReason=${reason}`;
    }
    return http.request(api, "delete");
  }

  static loadAuditLogs(appointmentId) {
    return http.request(API_ENDPOINTS.appointmentAudits(appointmentId));
  }

  static fetchPatientAppointments(patientId, page) {
    return http
      .request(API_ENDPOINTS.patientAppointments(patientId, page, 20))
      .then((response) => {
        if (response && response._embedded) {
          return {
            appointments: response._embedded.appointments || [],
            pageInfo: response.page,
          };
        }
        return {
          appointments: [],
          pageInfo: { number: -1 },
        };
      });
  }

  static fetchClinics = (page, filter) => {
    let {
      specialityCode,
      scheduleId,
      assigneeId,
      assigneeReferenceType,
      clinicDateFrom,
      clinicDateTo,
    } = filter;

    let api = API_ENDPOINTS.clinics() + `?page=${page}&size=500`;
    if (specialityCode) {
      api += `&specialityCode=${specialityCode}`;
    }
    if (scheduleId) {
      api += `&scheduleId=${scheduleId}`;
    }
    if (assigneeId) {
      if (assigneeReferenceType === "CARE_TEAM") {
        api += `&taskAssigneeCareteamId=${assigneeId}`;
      } else {
        api += `&taskAssigneeStaffId=${assigneeId}`;
      }
    }
    if (clinicDateFrom) {
      api += `&clinicDateFrom=${moment(clinicDateFrom).format("DDMMYYYY")}`;
    }
    if (clinicDateTo) {
      api += `&clinicDateTo=${moment(clinicDateTo).format("DDMMYYYY")}`;
    }

    return http.request(api).then((response) => {
      if (response && response._embedded) {
        return {
          data: response._embedded.clinics || [],
          loadMore: response.page.totalPages > page + 1,
          total: response.page.totalElements,
        };
      } else {
        return {
          data: [],
          loadMore: false,
        };
      }

      // if (response && response._embedded) {
      //   return {
      //     clinics: response._embedded.clinics || [],
      //     pageInfo: response.page
      //   };
      // }
      // return {
      //   clinics: [],
      //   pageInfo: { number: -1 }
      // };
    });
  };

  static fetchClinic = (clinicId, scheduleId, clinicDate, start, end) => {
    let api = `${API_ENDPOINTS.clinics(clinicId)}?scheduleId=${scheduleId}`;

    if (start && end) {
      api += `&clinicDateFrom=${start}&clinicDateTo=${end}`;
    } else if (clinicDate) {
      api += `&clinicDate=${clinicDate}`;
    }

    return http.request(api);
  };

  static fetchClinicAppointments = (
    page,
    scheduleId,
    clinicDate,
    clinicStat,
    clinicStatValue,
    sortBy,
    sortDir,
    filter,
    start,
    end
  ) => {
    let api = `${API_ENDPOINTS.appointments}?page=${page}&size=100&scheduleId=${scheduleId}`;

    // Use date range if provided, otherwise use single date
    if (start && end) {
      api += `&start=${start}&end=${end}`;
    } else if (clinicDate) {
      api += `&clinicDate=${clinicDate}`;
    }

    if (clinicStat && clinicStatValue) {
      api += `&${clinicStat}=${clinicStatValue}`;
    }
    if (sortBy) {
      api += `&sortBy=${sortBy}`;
    }
    if (sortDir) {
      api += `&sortDir=${sortDir}`;
    }
    if (filter) {
      if (filter.assigneeId) {
        api += `&staffId=${filter.assigneeId}`;
      }
      if (filter.specialityCode) {
        api += `&specialityCode=${filter.specialityCode}`;
      }
    }

    return http.request(api).then((response) => {
      if (response && response._embedded) {
        return {
          appointments: response._embedded.appointments || [],
          pageInfo: response.page,
        };
      }
      return {
        patients: [],
        pageInfo: { number: -1 },
      };

      // if (data && data._embedded) {
      //   let appointments = data._embedded.appointments || [];
      //   appointments.forEach(appointment => {
      //     appointment.start = new Date(appointment.start);
      //     appointment.end = new Date(appointment.end);
      //   });
      //
      //   return appointments;
      // }
      // return [];
    });
  };

  static importClinicAppointments = (csvFile) => {
    const formData = new FormData();
    formData.append("files", csvFile);

    return http
      .request(
        API_ENDPOINTS.appointmentsImportCSV() + "?ignoreDuplicates=true",
        "post",
        formData,
        null,
        (progressEvent) => {
          const uploadProgress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          store.dispatch(showProgress(uploadProgress));
        },
        120000
      )
      .then((response) => {
        store.dispatch(closeProgress());
        let strings = [];
        if (response._embedded && response._embedded.strings) {
          strings = response._embedded.strings || [];
        }
        return strings;
      })
      .catch((e) => {
        store.dispatch(closeProgress());
      });
  };

  static fetchClinicReport(clinicId, scheduleId, clinicDate, send) {
    let api =
      API_ENDPOINTS.clinics(clinicId) +
      `?scheduleId=${scheduleId}&clinicDate=${clinicDate}`;
    if (send) {
      api += "&sendDetailedReport=true";
    } else {
      api += "&withDetailedReport=true";
    }
    return http.request(api);
  }

  static revertCancel(appointmentId) {
    return http.request(
      API_ENDPOINTS.revertAppointmentCancel(appointmentId),
      "POST",
      {}
    );
  }

  static importSchedules = (csvFile) => {
    const formData = new FormData();
    formData.append("files", csvFile);

    return http
      .request(
        API_ENDPOINTS.csvSchedules(),
        "post",
        formData,
        null,
        (progressEvent) => {
          const uploadProgress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          store.dispatch(showProgress(uploadProgress));
        },
        120000
      )
      .then((response) => {
        store.dispatch(closeProgress());
        let strings = [];
        if (response._embedded && response._embedded.strings) {
          strings = response._embedded.strings || [];
        }
        return strings;
      })
      .catch((e) => {
        store.dispatch(closeProgress());
      });
  };

  static fetchClinicReports = (filter, sort, output) => {
    let api =
      API_ENDPOINTS.clinicReports() +
      `?` +
      AppointmentsService.getClinicsReportCriteria(filter, sort);

    if (output) {
      api += `&output=${output}`;
    }

    return http.request(api);
  };

  static fetchClinicReportsCSV = (filter, sort) => {
    let api =
      API_ENDPOINTS.clinicReportsCSV() +
      "?" +
      AppointmentsService.getClinicsReportCriteria(filter, sort);
    return http.request(api);
  };

  static getClinicsReportCriteria(filter, sort) {
    let {
      specialityCode,
      scheduleId,
      assigneeId,
      assigneeReferenceType,
      clinicDateFrom,
      clinicDateTo,
      callDateFrom,
      callDateTo,
    } = filter;

    let api = "";

    if (specialityCode) {
      api += `&specialityCode=${specialityCode}`;
    }
    if (scheduleId) {
      api += `&scheduleId=${scheduleId}`;
    }
    if (assigneeId) {
      if (assigneeReferenceType === "CARE_TEAM") {
        api += `&taskAssigneeCareteamId=${assigneeId}`;
      } else {
        api += `&taskAssigneeStaffId=${assigneeId}`;
      }
    }
    if (clinicDateFrom) {
      api += `&clinicDateFrom=${moment(clinicDateFrom).format("DDMMYYYY")}`;
    }
    if (clinicDateTo) {
      api += `&clinicDateTo=${moment(clinicDateTo).format("DDMMYYYY")}`;
    }

    if (callDateFrom) {
      api += `&callDateFrom=${moment(callDateFrom).format("DDMMYYYY")}`;
    }
    if (callDateTo) {
      api += `&callDateTo=${moment(callDateTo).format("DDMMYYYY")}`;
    }

    if (sort) {
      if (sort.column) {
        api += `&sortColumn=${sort.column}`;
      }
      if (sort.order) {
        api += `&sortOrder=${sort.order}`;
      }
    }
    return api;
  }

  static fetchClinicsUploadReports = (page, size) => {
    return http
      .request(API_ENDPOINTS.clinicUploadReport + `?page=${page}&size=${size}`)
      .then((response) => {
        if (response && response._embedded) {
          return {
            reports: response._embedded.cSVDataLoadAudits || [],
            pageInfo: response.page,
          };
        }
        return {
          reports: [],
          pageInfo: { number: -1 },
        };
      });
  };

  static cloneTemplate(templateId, data) {
    return http.request(
      API_ENDPOINTS.apptTemplateClone(templateId),
      "post",
      data
    );
  }

  static notifyStaff(clinicId, scheduleId, clinicDate, payload) {
    return http.request(
      API_ENDPOINTS.clinicNotifyStaff(clinicId, scheduleId, clinicDate),
      "post",
      payload
    );
  }

  static updateStaffParticipants(clinicId, payload) {
    return http.request(
      API_ENDPOINTS.clinicUpdateStaffParticipants(clinicId),
      "patch",
      payload
    );
  }

  static getAllSmartSchedulers(patientTypeId, page = 0, size = 20) {
    return http.request(
      API_ENDPOINTS.smartSchedulers(patientTypeId) +
        `?page=${page}&size=${size}`
    );
  }
  static addSmartScheduler(patientTypeId, data) {
    return http.request(
      API_ENDPOINTS.smartSchedulers(patientTypeId),
      "post",
      data
    );
  }

  static updateSmartScheduler(patientTypeId, schedulerId, data) {
    return http.request(
      API_ENDPOINTS.smartSchedulers(patientTypeId) + `/${schedulerId}`,
      "patch",
      data
    );
  }

  static deleteSmartScheduler(patientTypeId, schedulerId) {
    return http.request(
      API_ENDPOINTS.smartSchedulers(patientTypeId) + `/${schedulerId}`,
      "delete"
    );
  }

  static getAppointmentAddOns(patientTypeId) {
    return http
      .request(API_ENDPOINTS.appointmentAddOns(patientTypeId))
      .then((data) => data?._embedded?.appointmentAddOnTemplates || []);
  }

  static addAppointmentAddOn(patientTypeId, data) {
    return http.request(
      API_ENDPOINTS.appointmentAddOns(patientTypeId),
      "post",
      data
    );
  }

  static updateAppointmentAddOn(patientTypeId, addOnId, data) {
    return http.request(
      API_ENDPOINTS.appointmentAddOns(patientTypeId) + `/${addOnId}`,
      "patch",
      data
    );
  }

  static deleteAppointmentAddOn(patientTypeId, addOnId) {
    return http.request(
      API_ENDPOINTS.appointmentAddOns(patientTypeId) + `/${addOnId}`,
      "delete"
    );
  }

  static convertPdfToRegimen(patientTypeId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return http.request(
      API_ENDPOINTS.smartSchedulers(patientTypeId) + '/convertFromPdf',
      'post',
      formData
    );
  }
}

const appointmentsService = AppointmentsService;
export default appointmentsService;
